import type { ButtonHTMLAttributes } from 'react';
import { TableCell, TableRow } from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon, PencilIcon, UploadIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { ManagedCustomDashboardTab } from '@/lib/api/custom-dashboard';
import { DATA_UPDATE_BLOCKER_HINTS, dataUpdateBlocker } from './dataUpdateAvailability';
import type { CancelRequestTarget, DataUpdateRequestTarget } from './dataUpdateTargets';
import { formatShortDateTime } from './formatShortDateTime';
import { TabDataUpdateControls } from './TabDataUpdateControls';
import { TabDataUpdateStatus } from './TabDataUpdateStatus';
import { TabPublishStatus } from './TabPublishStatus';
import { TabMoveMenu, TabRowMenu, type TabMoveTarget } from './TabRowMenus';
import { StatusNote } from './TabStatusParts';
import { TabUpdateSettingsStatus } from './TabUpdateSettingsStatus';

export interface TabRowProps {
  tab: ManagedCustomDashboardTab;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  /** 上下の並べ替えを出すか（グループ未指定のタブは順を持たないため出さない）。 */
  canReorder: boolean;
  /** 移動先の選択肢（このタブのグループ以外）。 */
  moveTargets: TabMoveTarget[];
  /** 配置の保存中など、並べ替え・移動・削除を受け付けないとき。 */
  isLayoutBusy: boolean;
  isEditing: boolean;
  editingName: string;
  isRenaming: boolean;
  onEditingNameChange: (value: string) => void;
  onStartRename: () => void;
  onRenameConfirm: () => void;
  onRenameCancel: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  /** 選んだ移動先の `TabMoveTarget.value` で呼ぶ。 */
  onMoveToGroup: (value: string) => void;
  onUploadRequest: () => void;
  onHistoryRequest: () => void;
  /** 定期更新の設定ダイアログを開く。 */
  onUpdateSettingsRequest: () => void;
  /** 公開前の revision をプレビューする。 */
  onPreviewRequest: (revision: number) => void;
  onDeleteRequest: () => void;
  onDataUpdateRequest: (target: DataUpdateRequestTarget) => void;
  onCancelRequest: (target: CancelRequestTarget) => void;
  isCancellingDataUpdate: boolean;
}

function OrderButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className="inline-flex h-[18px] w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
      {...props}
    />
  );
}

/**
 * タブ一覧表の 1 行。列は 順序 / タブ / 公開状態 / 直近のデータ更新 / 定期更新 / 操作。定期更新の設定は
 * 「定期更新」列のチップから開く。操作列に常に出すのはデータ更新とアップロード（グループ未指定の行はグループへの移動も）
 * だけにし、ほかは「⋯」メニューにまとめる。
 */
export function TabRow({
  tab,
  index,
  isFirst,
  isLast,
  canReorder,
  moveTargets,
  isLayoutBusy,
  isEditing,
  editingName,
  isRenaming,
  onEditingNameChange,
  onStartRename,
  onRenameConfirm,
  onRenameCancel,
  onMoveUp,
  onMoveDown,
  onMoveToGroup,
  onUploadRequest,
  onHistoryRequest,
  onUpdateSettingsRequest,
  onPreviewRequest,
  onDeleteRequest,
  onDataUpdateRequest,
  onCancelRequest,
  isCancellingDataUpdate,
}: TabRowProps) {
  const isUngrouped = tab.groupId === null;
  const blocker = dataUpdateBlocker(tab);
  const updatedAt = formatShortDateTime(tab.updatedAt);

  return (
    <TableRow className="group">
      {canReorder && (
        <TableCell align="center">
          <div className="flex flex-col items-center">
            <OrderButton
              aria-label={`${tab.name}を上に移動`}
              disabled={isFirst || isLayoutBusy}
              onClick={onMoveUp}
            >
              <ChevronUpIcon size={14} aria-hidden />
            </OrderButton>
            <span className="font-mono text-xs leading-4 text-muted-foreground">{index + 1}</span>
            <OrderButton
              aria-label={`${tab.name}を下に移動`}
              disabled={isLast || isLayoutBusy}
              onClick={onMoveDown}
            >
              <ChevronDownIcon size={14} aria-hidden />
            </OrderButton>
          </div>
        </TableCell>
      )}
      <TableCell>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Input
              aria-label="タブ名"
              className="h-8 max-w-xs"
              value={editingName}
              onChange={(event) => onEditingNameChange(event.target.value)}
            />
            <Button
              size="sm"
              onClick={onRenameConfirm}
              disabled={isRenaming || editingName.trim().length === 0}
            >
              確定
            </Button>
            <Button size="sm" variant="ghost" onClick={onRenameCancel} disabled={isRenaming}>
              取消
            </Button>
          </div>
        ) : (
          <>
            <div className="flex min-w-0 items-center gap-0.5">
              <span className="truncate text-sm font-semibold">{tab.name}</span>
              {/* 名前変更は「⋯」にもあるため、行に乗ったとき（とフォーカス時）だけ見せる */}
              <Button
                size="icon"
                variant="ghost"
                className="size-7 shrink-0 text-muted-foreground opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
                aria-label={`${tab.name}の名前を変更`}
                onClick={onStartRename}
              >
                <PencilIcon size={14} aria-hidden />
              </Button>
            </div>
            {updatedAt && <StatusNote>最終更新 {updatedAt}</StatusNote>}
          </>
        )}
      </TableCell>
      <TableCell>
        <TabPublishStatus
          tab={tab}
          onPreviewRequest={onPreviewRequest}
          onHistoryRequest={onHistoryRequest}
        />
      </TableCell>
      <TableCell>
        <TabDataUpdateStatus
          tabId={tab.tabId}
          tabName={tab.name}
          latestDataUpdate={tab.latestDataUpdate}
          onRequestCancel={onCancelRequest}
          isCancelling={isCancellingDataUpdate}
          idleHint={
            blocker === null || blocker === 'running'
              ? undefined
              : DATA_UPDATE_BLOCKER_HINTS[blocker]
          }
        />
      </TableCell>
      <TableCell>
        <TabUpdateSettingsStatus tab={tab} onRequest={onUpdateSettingsRequest} />
      </TableCell>
      <TableCell align="right">
        <div className="flex items-center justify-end gap-1.5">
          {isUngrouped && (
            <TabMoveMenu
              tabName={tab.name}
              moveTargets={moveTargets}
              isLayoutBusy={isLayoutBusy}
              onMoveToGroup={onMoveToGroup}
            />
          )}
          <TabDataUpdateControls tab={tab} onRequestUpdate={onDataUpdateRequest} />
          <Button
            size="sm"
            variant="outline"
            aria-label={`${tab.name}にアップロード`}
            onClick={onUploadRequest}
          >
            <UploadIcon size={16} aria-hidden />
            アップロード
          </Button>
          <TabRowMenu
            tabName={tab.name}
            moveTargets={isUngrouped ? undefined : moveTargets}
            isLayoutBusy={isLayoutBusy}
            onStartRename={onStartRename}
            onHistoryRequest={onHistoryRequest}
            onMoveToGroup={onMoveToGroup}
            onDeleteRequest={onDeleteRequest}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}
